import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { MAIN_NAV, STAFF_PAGES, type NavItem } from "../../nav";
import { selectCount, useCart } from "../../store/cart";
import { useMe, useSignOut } from "../../api/hooks";
import { CartIcon, ClipboardIcon, UserIcon, ChevronDown, CloseIcon, MenuIcon, SearchIcon } from "../Icons";
import styles from "./Layout.module.css";
import { SearchBox } from "../search/SearchBox";
import { Logo } from "./Logo";
import { MobileDrawer } from "./MobileDrawer";
import { features } from "../../features";
import { CONTENT } from "../../content";

/** site.json `headerSearch: "box"`: a search box in the header on desktop; smaller screens keep the icon. */
const SEARCH_BOX = CONTENT.site.headerSearch === "box";

function isActive(item: NavItem, pathname: string): boolean {
  if (item.to === "/") return pathname === "/";
  return pathname.startsWith(item.to) || Boolean(item.children?.some((c) => isActive(c, pathname)));
}

function MainNav() {
  const { pathname } = useLocation();
  return (
    <nav className={styles.mainNav} aria-label="Main">
      <ul>
        {MAIN_NAV.map((item) => (
          <li key={item.label} className={item.children ? styles.hasDropdown : undefined}>
            <NavLink
              to={item.to}
              end={item.to === "/"}
              className={isActive(item, pathname) ? styles.navActive : undefined}
              aria-haspopup={item.children ? "true" : undefined}
            >
              {item.label}
              {item.children && <ChevronDown className={styles.chevron} />}
            </NavLink>
            {item.children && (
              <ul className={styles.dropdown}>
                {item.children.map((child) => (
                  <li key={child.to}>
                    <NavLink to={child.to}>{child.label}</NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </nav>
  );
}

function HeaderSearchBox({ onClose, autoFocus }: { onClose?: () => void; autoFocus?: boolean }) {
  const navigate = useNavigate();
  return (
    <SearchBox
      autoFocus={autoFocus}
      submitLabel="Search"
      onSubmit={(q) => {
        navigate(`/search?q=${encodeURIComponent(q)}`);
        onClose?.();
      }}
      onPick={(s) => {
        navigate(`/products/${encodeURIComponent(s.sku)}`);
        onClose?.();
      }}
      onEscape={onClose}
    />
  );
}

function SearchPanel({ onClose }: { onClose: () => void }) {
  return (
    <div className={`${styles.searchPanel} ${SEARCH_BOX ? styles.searchPanelNarrow : ""}`}>
      <div className="container">
        <HeaderSearchBox autoFocus onClose={onClose} />
      </div>
    </div>
  );
}

/** A menu under a header button: it closes on navigation, on a click or focus outside, and on Escape. */
function useHeaderMenu() {
  const [open, setOpen] = useState(false);
  const menu = useRef<HTMLDivElement>(null);
  const toggle = useRef<HTMLButtonElement>(null);

  const { pathname } = useLocation();
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setOpen(false);
  }

  // Close on a click or focus outside (tabbing away), or on Escape (back to the button).
  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (!menu.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      toggle.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("focusin", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("focusin", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  return { open, setOpen, menu, toggle };
}

/** The staff pages, for staff with the Admin role. */
function AdminMenu() {
  const { data: me } = useMe();
  const { open, setOpen, menu, toggle } = useHeaderMenu();

  if (!me?.isAdmin || !features.staffTools) return null;
  return (
    <div className={styles.headerMenu} ref={menu}>
      <button
        ref={toggle}
        type="button"
        className={styles.iconButton}
        aria-label="Admin"
        title="Admin"
        aria-expanded={open}
        aria-controls="admin-menu"
        onClick={() => setOpen((o) => !o)}
      >
        <ClipboardIcon alt="Clipboard" />
      </button>
      {open && (
        <div id="admin-menu" className={`${styles.headerDropdown} ${styles.adminDropdown}`}>
          <ul>
            {STAFF_PAGES.map(({ label, to }) => (
              <li key={to}>
                <Link to={to} onClick={() => setOpen(false)}>
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * Signed out, the person icon goes to sign-in. Signed in, it opens the account menu: Profile,
 * Orders, and Sign out, always last.
 */
function AccountMenu() {
  const { data: me } = useMe();
  const signOut = useSignOut();
  const { open, setOpen, menu, toggle } = useHeaderMenu();
  const [signingOut, setSigningOut] = useState(false);
  const [failed, setFailed] = useState(false);

  if (!features.accounts) return null;
  if (!me) {
    return (
      <Link to="/account/sign-in" className={styles.iconButton} aria-label="Sign in" title="Sign in">
        <UserIcon alt="Person" />
      </Link>
    );
  }

  const label = `Your account${me.name ? `, ${me.name}` : ""}`;
  const leave = async () => {
    setSigningOut(true);
    setFailed(false);
    try {
      await signOut();
    } catch {
      setFailed(true);
      setSigningOut(false);
    }
  };
  return (
    <div className={styles.headerMenu} ref={menu}>
      <button
        ref={toggle}
        type="button"
        className={styles.iconButton}
        aria-label={label}
        title={label}
        aria-expanded={open}
        aria-controls="account-menu"
        onClick={() => {
          setOpen((o) => !o);
          setFailed(false);
        }}
      >
        <UserIcon alt="Person" />
      </button>
      {open && (
        <div id="account-menu" className={styles.headerDropdown}>
          <div className={styles.accountWho}>
            Signed in as <strong>{me.name || me.email || "you"}</strong>
          </div>
          <ul>
            <li>
              <Link to="/account">Profile</Link>
            </li>
            <li>
              <Link to="/account/orders">Orders</Link>
            </li>
            <li>
              <button type="button" onClick={leave} disabled={signingOut}>
                {signingOut ? "Signing out…" : "Sign out"}
              </button>
            </li>
          </ul>
          {failed && (
            <p role="alert" className={styles.accountError}>
              We couldn&rsquo;t sign you out. Please try again.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export function Header() {
  const count = useCart(selectCount);
  const [searchOpen, setSearchOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { pathname } = useLocation();

  // Close the search panel and drawer on navigation. Adjusting state during render
  // (instead of in an effect) avoids an extra render pass.
  const [lastPath, setLastPath] = useState(pathname);
  if (pathname !== lastPath) {
    setLastPath(pathname);
    setSearchOpen(false);
    setDrawerOpen(false);
  }

  return (
    <header className={styles.header}>
      <div className={`container ${styles.headerInner} ${SEARCH_BOX ? styles.withSearchBox : ""}`}>
        <Logo />
        <MainNav />
        {SEARCH_BOX && (
          <div className={styles.headerSearch}>
            <HeaderSearchBox />
          </div>
        )}
        <div className={styles.headerActions}>
          <button
            type="button"
            className={`${styles.iconButton} ${SEARCH_BOX ? styles.searchToggle : ""}`}
            aria-label={searchOpen ? "Close search" : "Search"}
            title={searchOpen ? "Close search" : "Search"}
            aria-expanded={searchOpen}
            onClick={() => setSearchOpen((o) => !o)}
          >
            {searchOpen ? <CloseIcon alt="Close" /> : <SearchIcon alt="Magnifying glass" />}
          </button>
          <AdminMenu />
          <AccountMenu />
          <Link
            to="/cart"
            className={styles.iconButton}
            aria-label={`Cart, ${count} item${count === 1 ? "" : "s"}`}
            title="Cart"
          >
            <CartIcon alt="Shopping cart" />
            {count > 0 && <span className={styles.cartBadge}>{count > 99 ? "99+" : count}</span>}
          </Link>
          <button
            type="button"
            className={`${styles.iconButton} ${styles.menuToggle}`}
            aria-label="Menu"
            title="Menu"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
          >
            <MenuIcon alt="Three lines" />
          </button>
        </div>
      </div>
      {searchOpen && <SearchPanel onClose={() => setSearchOpen(false)} />}
      <MobileDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </header>
  );
}
